import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, X, Check, ChevronDown, Globe } from "lucide-react";
import { COUNTRIES } from "@/data/countries";

// Popular countries pinned to the top of the list.
const POPULAR = ["US", "NG", "CO", "JM", "GB", "MX", "BR", "GH"];

export default function CountryPicker({
  value,
  onChange,
  triggerTestId = "country-picker",
  optionPrefix = "country-option",
  searchTestId = "country-search",
  placeholder = "Select your country",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const selected = COUNTRIES.find((c) => c.code === value);

  useEffect(() => {
    if (open) {
      setQ("");
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      document.body.style.overflow = "hidden";
      return () => { clearTimeout(t); document.body.style.overflow = ""; };
    }
    document.body.style.overflow = "";
  }, [open]);

  const { searching, filtered, popular, rest } = useMemo(() => {
    const byName = [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
    const term = q.trim().toLowerCase();
    if (term) {
      return {
        searching: true,
        filtered: byName.filter(
          (c) => c.name.toLowerCase().includes(term) || c.code.toLowerCase() === term || c.language.toLowerCase().includes(term)
        ),
        popular: [], rest: [],
      };
    }
    return {
      searching: false,
      filtered: [],
      popular: POPULAR.map((code) => COUNTRIES.find((c) => c.code === code)).filter(Boolean),
      rest: byName.filter((c) => !POPULAR.includes(c.code)),
    };
  }, [q]);

  const pick = (code) => { onChange(code); setOpen(false); };

  const Row = ({ c }) => (
    <button
      type="button"
      data-testid={`${optionPrefix}-${c.code}`}
      onClick={() => pick(c.code)}
      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/10 active:bg-white/15 transition-colors"
    >
      <span className="text-xl w-7 text-center shrink-0">{c.flag}</span>
      <span className="flex-1 text-[#F1F1F5] text-[15px] truncate">{c.name}</span>
      <span className="text-xs text-[#71717A] shrink-0">{c.language}</span>
      {value === c.code && <Check className="w-4 h-4 text-[#00D9FF] shrink-0" />}
    </button>
  );

  return (
    <>
      <button
        type="button"
        data-testid={triggerTestId}
        onClick={() => setOpen(true)}
        className="w-full h-12 flex items-center justify-between gap-2 bg-[#0F1636] border border-white/10 rounded-xl px-3 text-left transition-colors hover:border-[#00D9FF]/60 focus:outline-none focus:border-[#00D9FF]"
      >
        <span className="flex items-center gap-2 truncate">
          {selected ? (
            <>
              <span className="text-lg">{selected.flag}</span>
              <span className="text-[#F1F1F5] text-[15px] truncate">{selected.name}</span>
            </>
          ) : (
            <>
              <Globe className="w-4 h-4 text-[#71717A]" />
              <span className="text-[#71717A] text-[15px]">{placeholder}</span>
            </>
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-[#71717A] shrink-0" />
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="country-picker-sheet">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:max-w-md bg-[#0A0E27] border border-white/10 rounded-t-3xl sm:rounded-2xl max-h-[85vh] sm:max-h-[70vh] flex flex-col overflow-hidden shadow-2xl animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">
            <div className="flex items-center gap-2 p-3 border-b border-white/10 shrink-0">
              <div className="flex items-center gap-2 flex-1 h-11 bg-[#0F1636] border border-white/10 rounded-xl px-3 focus-within:border-[#00D9FF]">
                <Search className="w-4 h-4 text-[#71717A] shrink-0" />
                <input
                  ref={inputRef}
                  data-testid={searchTestId}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search country…"
                  className="flex-1 bg-transparent outline-none text-[#F1F1F5] text-[15px] placeholder:text-[#71717A]"
                />
              </div>
              <button type="button" data-testid="country-picker-close" onClick={() => setOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-full text-[#A0A0AB] hover:bg-white/10 shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain flex-1" style={{ WebkitOverflowScrolling: "touch" }}>
              {searching ? (
                filtered.length ? (
                  filtered.map((c) => <Row key={c.code} c={c} />)
                ) : (
                  <p className="text-center text-sm text-[#71717A] py-8">No countries match “{q}”.</p>
                )
              ) : (
                <>
                  <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-[#71717A]">Popular</p>
                  {popular.map((c) => <Row key={c.code} c={c} />)}
                  <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-[#71717A]">All countries</p>
                  {rest.map((c) => <Row key={c.code} c={c} />)}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
